import React, { useState, useEffect } from 'react';
import { Button } from '@mui/material';
import { Hotspot, HotspotItem } from './Viewer';

type HotspotEditorProps = {
  imageUrl: string;
  onHotspotsChange: (hotspots: Hotspot[]) => void;
};

const HotspotEditor: React.FC<HotspotEditorProps> = ({
  imageUrl,
  onHotspotsChange: _onHotspotsChange,
}) => {
  const [imgURL, setImgURL] = useState<string>(imageUrl || '');
  const [hotspots, setHotspots] = useState<Hotspot[]>([]);

  useEffect(() => {
    CustomElement.init((element, initContext) => {
      if (element && initContext) {
        if (element.value !== undefined && element.value !== null) {
          setHotspots((JSON.parse(element.value).hotspots as Hotspot[]) || []);
          setImgURL((JSON.parse(element.value).url as string) || '');
        }
      }
    });
  }, []);

  useEffect(() => {
    CustomElement.setHeight(500);
  }, []);

  // Sync imageUrl prop with imgURL state
  useEffect(() => {
    setImgURL(imageUrl || '');
  }, [imageUrl]);

  const handleButtonClick = (): void => {
    CustomElement.selectAssets({
      allowMultiple: false,
      fileType: 'images',
    }).then(assets => {
      if (assets && assets.length > 0) {
        CustomElement.getAssetDetails([assets[0].id]).then(assetDetails => {
          if (assetDetails && assetDetails.length > 0) {
            setImgURL(assetDetails[0].url);
            setTimeout(() => {
              saveHotspotDetails(assetDetails[0].url, hotspots);
            }, 1000);
          }
        });
      }
    });
  };

  const saveHotspotDetails = (url: string, hotspotsArr: Hotspot[]): void => {
    setTimeout(() => {
      const updatedHotspots = hotspotsArr.filter(
        hotspotsArr => hotspotsArr.description?.trim() !== '',
      );
      const updatedHotspotItem: HotspotItem = {
        url: url,
        hotspots: updatedHotspots,
      };
      CustomElement.setValue(JSON.stringify(updatedHotspotItem));
    }, 750);
  };

  const handleImageClick = (
    event: React.MouseEvent<HTMLImageElement, MouseEvent>,
  ): void => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100; // Percentage x-coordinate
    const y = ((event.clientY - rect.top) / rect.height) * 100; // Percentage y-coordinate

    const newHotspot: Hotspot = {
      x,
      y,
      description: '',
      color: { transparent: false, hex: '#000000' },
    };
    const updatedHotspots = [...hotspots, newHotspot];
    setHotspots(updatedHotspots);
  };

  const handleTextChange = (index: number, description: string): void => {
    const updatedHotspots = hotspots.map((hotspot, i) =>
      i === index ? { ...hotspot, description } : hotspot,
    );
    setHotspots(updatedHotspots);
  };
  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>,
    _index: number,
  ): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const updatedHotspots = hotspots.filter(
        hotspot => hotspot.description?.trim() !== '',
      );
      setHotspots(updatedHotspots);
      saveHotspotDetails(imgURL, updatedHotspots);
    }
  };

  return (
    <div style={{ width: '100%', textAlign: 'center' }}>
      <div style={{ marginBottom: '20px' }}>
        <Button
          size="large"
          variant="contained"
          sx={{ fontSize: '1.2rem' }}
          onClick={handleButtonClick}
        >
          Choose Asset Image
        </Button>
      </div>
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <img
          src={imgURL}
          alt="Hotspot Editor"
          style={{ maxWidth: '100%', cursor: 'crosshair' }}
          onClick={handleImageClick}
          hidden={!imgURL}
        />
        {hotspots.map((hotspot, index) => (
          <div
            key={index}
            style={{
              position: 'absolute',
              top: `${hotspot.y}%`,
              left: `${hotspot.x}%`,
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'red',
              width: '10px',
              height: '10px',
              borderRadius: '50%',
            }}
          >
            {' '}
            <input
              type="text"
              value={hotspot.description || ''}
              onChange={(e): void => handleTextChange(index, e.target.value)}
              onKeyPress={(e): void => handleKeyPress(e, index)}
              style={{
                position: 'absolute',
                top: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                fontSize: '10px',
                width: '200px',
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default HotspotEditor;
